"use client";

import { useState } from "react";
import Link from "next/link";
import { 
  Bell, 
  Calendar, 
  Download, 
  Eye, 
  ExternalLink, 
  Phone, 
  Mail, 
  Globe, 
  ShieldCheck, 
  CheckCircle2, 
  AlertTriangle,
  FileText,
  Building,
  Award,
  Share2,
  X,
  Smartphone,
  ChevronRight
} from "lucide-react";

export default function NoticesPage() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <main className="min-h-screen bg-[#f8fafc] text-gray-900">
      {/* Hero Banner */}
      <section className="relative bg-[#143674] text-white py-14 lg:py-20 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(247,148,30,0.15),transparent_50%)] pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-accent text-xs md:text-sm font-semibold mb-4 backdrop-blur-sm">
              <Bell className="w-4 h-4 animate-bounce" />
              <span>Official Institutional Circulars & Compliance Announcements</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-white">
              IAER Notice Board
            </h1>
            
            <p className="text-gray-200 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Official circulars, student welfare advisories, regulatory notifications, and 
              compliance disclosures from the Institute of Advance Education & Research (IAER).
            </p>

            {/* Compliance affiliations badges */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-4 text-xs font-medium text-gray-300">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/5 border border-white/10">
                <Award className="w-3.5 h-3.5 text-accent" /> AICTE Approved
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/5 border border-white/10">
                <Building className="w-3.5 h-3.5 text-accent" /> MoE’s Innovation Cell (MIC)
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/5 border border-white/10">
                <ShieldCheck className="w-3.5 h-3.5 text-accent" /> Institution’s Innovation Council (IIC)
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumbs & Category Bar */}
      <section className="border-b border-gray-200 bg-white sticky top-20 z-30 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <Link href="/" className="hover:text-[#143674] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-800 font-semibold">Notice Board</span>
          </nav>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 text-xs font-medium">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
                activeCategory === "all"
                  ? "bg-[#143674] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All Circulars
            </button>
            <button
              onClick={() => setActiveCategory("iic")}
              className={`px-3 py-1.5 rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                activeCategory === "iic"
                  ? "bg-[#143674] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
              IIC Compliance
            </button>
            <button
              onClick={() => setActiveCategory("student-welfare")}
              className={`px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
                activeCategory === "student-welfare"
                  ? "bg-[#143674] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Student Welfare
            </button>
            <button
              onClick={() => setActiveCategory("academic")}
              className={`px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
                activeCategory === "academic"
                  ? "bg-[#143674] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Academics
            </button>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-6xl mx-auto space-y-8">
          
          {/* Featured Notice Banner / Alert */}
          <div className="bg-gradient-to-r from-blue-900 to-[#143674] text-white rounded-2xl p-4 md:p-6 shadow-md border border-blue-800/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="p-2.5 bg-accent/20 border border-accent/40 rounded-xl text-accent shrink-0 mt-0.5">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="bg-accent text-white text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    Active Directive
                  </span>
                  <span className="text-gray-300 text-xs">
                    Ref: IAER/IIC-SWC/2026/091
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-bold text-white">
                  IIC & Student Welfare Compliance: Drug-Free Campus Initiative
                </h3>
                <p className="text-xs md:text-sm text-gray-200 mt-1">
                  Published in compliance with Ministry of Education&apos;s Innovation Cell (MIC), 
                  Institution&apos;s Innovation Council (IIC), and Narcotics Control Bureau (NCB).
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-start md:self-center shrink-0">
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold text-white border border-white/10 transition-colors"
                title="Share notice link"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>{copied ? "Copied!" : "Share"}</span>
              </button>
              <a
                href="/images/notice/manas_poster.png"
                download="IAER_MANAS_Drug_Free_Campus_Notice.png"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent hover:bg-accent/90 text-xs font-bold text-white uppercase tracking-wider shadow transition-all hover:-translate-y-0.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download Poster</span>
              </a>
            </div>
          </div>

          {/* Featured Poster & Details Grid */}
          {(activeCategory === "all" || activeCategory === "iic" || activeCategory === "student-welfare") && (
            <article className="bg-white rounded-2xl border border-gray-200/80 shadow-lg overflow-hidden transition-all duration-300">
              {/* Notice Card Top Header */}
              <div className="border-b border-gray-100 bg-gray-50/70 p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-800 bg-blue-100/80 px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                      <ShieldCheck className="w-3 h-3" />
                      IIC Compliance
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-100/80 px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                      <Award className="w-3 h-3" />
                      AICTE Approved Institution
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Calendar className="w-3.5 h-3.5" />
                      September 18, 2026
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-black text-gray-900 leading-snug">
                    SAY NO TO DRUGS, YES TO A BRIGHTER TOMORROW
                  </h2>
                  <p className="text-sm font-semibold text-gray-600 mt-1">
                    MANAS: National Narcotics Helpline (Madak Padarth Nishedh Rashtriya Sewa)
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setSelectedImage("/images/notice/manas_poster.png")}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#143674] hover:bg-[#102a5c] text-white text-xs font-semibold shadow-sm transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Full Size</span>
                  </button>
                  <a
                    href="/images/notice/manas_poster.png"
                    download="IAER_MANAS_Poster.png"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </a>
                </div>
              </div>

              {/* Notice Card Body */}
              <div className="p-5 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* Left / Poster Image Display */}
                  <div className="lg:col-span-5 flex flex-col items-center">
                    <div 
                      className="group relative w-full max-w-md rounded-xl overflow-hidden shadow-md border border-gray-200 cursor-pointer bg-gray-50 hover:shadow-xl transition-all"
                      onClick={() => setSelectedImage("/images/notice/manas_poster.png")}
                    >
                      <img
                        src="/images/notice/manas_poster.png"
                        alt="MANAS Drug Free Campus Official Poster - IAER IIC Compliance"
                        className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-medium text-sm">
                        <Eye className="w-5 h-5 text-accent" />
                        <span>Click to Enlarge Poster</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2 text-center">
                      *Click on the poster to view in high definition or download for reference.
                    </p>
                  </div>

                  {/* Right / Notice Details & Quick Contacts */}
                  <div className="lg:col-span-7 space-y-6">
                    <div>
                      <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        Official Directive & Institutional Scope
                      </h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        In strict adherence to the statutory guidelines issued by the{" "}
                        <strong className="text-gray-800">Ministry of Education (MoE)</strong>,{" "}
                        <strong className="text-gray-800">MoE&apos;s Innovation Cell (MIC)</strong>, and{" "}
                        <strong className="text-gray-800">AICTE</strong>, the{" "}
                        <strong className="text-gray-800">Institution&apos;s Innovation Council (IIC)</strong> at{" "}
                        <strong>IAER</strong> has implemented the comprehensive{" "}
                        <strong>MANAS (National Narcotics Helpline)</strong> initiative to maintain a 100% drug-free, safe, and empowering academic campus.
                      </p>
                    </div>

                    {/* Quick Helplines Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Helpline 1933 */}
                      <a
                        href="tel:1933"
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-red-100 bg-red-50/60 hover:bg-red-50 hover:border-red-300 transition-all group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-red-600 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-red-700">
                            24x7 Toll-Free Helpline
                          </p>
                          <p className="text-xl font-black text-gray-900 tracking-tight">
                            1933
                          </p>
                          <p className="text-[11px] text-gray-500">Anonymous, Safe & Supportive</p>
                        </div>
                      </a>

                      {/* Official Web Portal */}
                      <a
                        href="https://ncbmanas.gov.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-blue-100 bg-blue-50/60 hover:bg-blue-50 hover:border-blue-300 transition-all group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#143674] text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                          <Globe className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-[#143674]">
                            Official Web Portal
                          </p>
                          <p className="text-sm font-bold text-gray-900 group-hover:text-[#143674] flex items-center gap-1">
                            ncbmanas.gov.in
                            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                          </p>
                          <p className="text-[11px] text-gray-500">Report & Seek Assistance</p>
                        </div>
                      </a>

                      {/* Official Email */}
                      <a
                        href="mailto:info.ncbmanas@gov.in"
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-gray-100/80 transition-all group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gray-700 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-600">
                            Official Email Submission
                          </p>
                          <p className="text-sm font-semibold text-gray-900 break-all">
                            info.ncbmanas@gov.in
                          </p>
                          <p className="text-[11px] text-gray-500">Direct Inquiries & Reporting</p>
                        </div>
                      </a>

                      {/* UMANG App */}
                      <div className="flex items-center gap-3 p-3.5 rounded-xl border border-amber-200 bg-amber-50/60">
                        <div className="w-10 h-10 rounded-lg bg-accent text-white flex items-center justify-center shrink-0">
                          <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
                            Mobile Application
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            UMANG Mobile App
                          </p>
                          <p className="text-[11px] text-gray-600">Search: <strong className="text-accent">MANAS</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Official Channels Summary Table */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-xs">
                      <div className="bg-gray-100 px-4 py-2.5 border-b border-gray-200">
                        <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                          Official MANAS Reporting & Support Scope
                        </h5>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2.5">Channel / Platform</th>
                              <th className="px-4 py-2.5">Contact Details & Scope</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 text-gray-700">
                            <tr>
                              <td className="px-4 py-2.5 font-medium text-gray-900">24x7 Toll-Free National Helpline</td>
                              <td className="px-4 py-2.5">
                                <span className="font-bold text-red-600">1933</span> (Anonymous reporting, confidential tips & counseling)
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2.5 font-medium text-gray-900">Official Web Portal</td>
                              <td className="px-4 py-2.5 text-blue-700 font-medium">ncbmanas.gov.in</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2.5 font-medium text-gray-900">Official Email Submission</td>
                              <td className="px-4 py-2.5">info.ncbmanas@gov.in</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2.5 font-medium text-gray-900">Mobile Application Access</td>
                              <td className="px-4 py-2.5">UMANG Mobile App (Search: MANAS)</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Key Core Commitments */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                      <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-2.5">
                        Campus Safeguard Protocols & Support Pillars
                      </h5>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span><strong>100% Confidentiality:</strong> Identity of change agents remains strictly protected.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span><strong>Anonymous Reporting:</strong> Report illicit activities or suspicious conduct safely.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span><strong>Rehabilitation & Counseling:</strong> Expert mental health & de-addiction assistance.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span><strong>Aware Citizens:</strong> Together for a healthier, drug-free campus community.</span>
                        </li>
                      </ul>
                    </div>

                  </div>
                </div>
              </div>

              {/* Notice Footer */}
              <div className="bg-gray-50 border-t border-gray-100 p-4 px-6 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
                <div>
                  Issued by: <strong>Institution&apos;s Innovation Council (IIC) & Student Welfare Committee</strong>, IAER Kolkata.
                </div>
                <div>
                  Regulatory Compliance: <strong>AICTE / MoE Innovation Cell / NCB</strong>
                </div>
              </div>
            </article>
          )}

          {/* Additional Institutional Circulars Placeholder */}
          {(activeCategory === "all" || activeCategory === "academic") && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-blue-50 rounded-lg text-[#143674] shrink-0 mt-1">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-blue-100 text-[#143674] text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                      Academic Notice
                    </span>
                    <span className="text-xs text-gray-500">Academic Year 2026-27</span>
                  </div>
                  <h4 className="text-base font-bold text-gray-900">
                    Academic Calendar & Semester Schedule Notification
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Official schedule covering semester commencement, internal assessments, and holidays.
                  </p>
                </div>
              </div>

              <Link
                href="/academics#academics-calendar"
                className="inline-flex items-center gap-1 text-xs font-bold text-[#143674] hover:text-accent transition-colors self-start md:self-center shrink-0"
              >
                <span>View Academic Calendar</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Statutory Verification / Compliance Note */}
          <div className="p-5 rounded-2xl bg-amber-50/70 border border-amber-200/80 text-xs text-amber-900 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-gray-900">
                Institutional Innovation & Statutory Compliance Note
              </p>
              <p className="mt-1 leading-relaxed text-gray-700">
                This notice board is maintained by the Institute of Advance Education & Research (IAER) for transparent dissemination 
                of statutory advisories, academic schedules, anti-drug compliance directives, and AICTE/UGC mandates. 
                For formal verifications or grievances, please contact the Registrar’s office or Student Welfare Cell at{" "}
                <a href="mailto:admission@iaer.ac.in" className="font-bold underline text-[#143674]">
                  admission@iaer.ac.in
                </a>.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Fullscreen Lightbox Modal for Poster */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3.5 px-5 border-b border-gray-200 bg-gray-50">
              <span className="text-xs md:text-sm font-bold text-gray-800">
                MANAS Drug-Free Campus Notice — IAER Official Poster
              </span>
              <div className="flex items-center gap-2">
                <a
                  href={selectedImage}
                  download="IAER_MANAS_Poster.png"
                  className="p-1.5 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                  title="Download Image"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="p-1.5 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Image Container */}
            <div className="overflow-auto p-2 bg-gray-900 flex items-center justify-center">
              <img
                src={selectedImage}
                alt="Enlarged Notice Poster"
                className="max-h-[78vh] w-auto object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
